/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { computed, DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  adminToggleClusterFeatures,
  clusterById,
  confirmClusterActivation,
  confirmClusterDeactivation,
  detachClusterIntent,
  isNebulaTeamUserAccess,
  teams as teamsSelector,
} from '@app/core/ngrx';
import { AppState } from '@app/core/types';
import { canClientsConnectToCluster } from '@app/domain/cluster';
import {
  getClusterStatus,
  isAWSDataEncryptedCluster,
  isClusterActive,
  isNebulaCluster,
  isNebulaInStatus,
  isPrivateLinkNebulaCluster,
} from '@app/domain/clusters';
import { isClusterInNebulaTeam, isClusterPersonal, isClusterTeamOwnedByMe, isMeInNebulaTeam } from '@app/domain/teams';
import { Store } from '@ngrx/store';
import { isCluster2x, isCluster3x } from '@shared/domain/cluster';
import { ClusterFeatures, ClusterId, ClusterInfo, ClusterStatus } from '@shared/types/cluster';
import { NebulaClusterStatus } from '@shared/types/nebula';
import { Team } from '@shared/types/team';
import { resumeSuspendedNebulaClusterIntent, suspendNebulaClusterIntent } from '../ngrx/cluster-management.actions';

const isClusterInPersonalTeam = (cluster: ClusterInfo, teams: Team[] | null) =>
  !teams ? false : isClusterPersonal(teams, cluster);

@Injectable({ providedIn: 'root' })
export class MenuClusterActionsService {
  constructor(private store: Store<AppState>) {
    this.handleNebulaTeamUserAccess();
  }
  private destroyRef = inject(DestroyRef);

  private hasNebulaTeamUserAccess: boolean = false;
  public isCluster2x = isCluster2x;

  private handleNebulaTeamUserAccess() {
    this.store
      .select(isNebulaTeamUserAccess)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => (this.hasNebulaTeamUserAccess = value));
  }

  activateCluster(clusterId: string) {
    this.store.dispatch(confirmClusterActivation({ clusterId }));
  }

  deactivateCluster(clusterId: string) {
    this.store.dispatch(confirmClusterDeactivation({ clusterId }));
  }

  isMenuVisible = (cluster: ClusterInfo): boolean =>
    !isNebulaCluster(cluster) ||
    !isNebulaInStatus(cluster, [ClusterStatus.DESTROYING, ClusterStatus.DESTROYED, ClusterStatus.FAILED]) ||
    this.hasNebulaTeamUserAccess;

  /* Share */
  isShareButtonVisible(cluster: ClusterInfo): boolean {
    return isNebulaCluster(cluster) ? isClusterActive(cluster) : true;
  }

  isShareButtonDisabled(cluster: ClusterInfo, teams: Team[] | null): boolean {
    return !isClusterInPersonalTeam(cluster, teams) || getClusterStatus(cluster) === ClusterStatus.PROVISIONING;
  }

  isShareTooltipDisabled(cluster: ClusterInfo, teams: Team[] | null): boolean {
    return isClusterInPersonalTeam(cluster, teams) || getClusterStatus(cluster) === ClusterStatus.PROVISIONING;
  }

  /* Nebula Connection Info */
  isNebulaConnectionInfoButtonVisible(cluster: ClusterInfo): boolean {
    return isNebulaCluster(cluster) && isClusterActive(cluster);
  }

  /* Manage Cluster Users */
  isManageClusterUsersButtonVisible(cluster: ClusterInfo): boolean {
    return isNebulaCluster(cluster) && isClusterActive(cluster);
  }

  isManageUsersButtonDisabled(cluster: ClusterInfo): boolean {
    return getClusterStatus(cluster) !== ClusterStatus.ACTIVE;
  }

  /* Data Encryption */
  isDataEncryptionButtonVisible(cluster: ClusterInfo, teams: Team[] | null, hasKmsFeature: boolean | null): boolean {
    return (
      isNebulaCluster(cluster) &&
      isClusterActive(cluster) &&
      !!hasKmsFeature &&
      isAWSDataEncryptedCluster(cluster) &&
      (isClusterInPersonalTeam(cluster, teams) || (!!teams && isMeInNebulaTeam(teams)))
    );
  }

  isDataEncryptionButtonDisabled(cluster: ClusterInfo): boolean {
    return getClusterStatus(cluster) !== ClusterStatus.ACTIVE;
  }

  /* Edit Access List */
  isEditAccessListButtonVisible(cluster: ClusterInfo, teams: Team[] | null): boolean {
    return (
      isNebulaCluster(cluster) &&
      !isPrivateLinkNebulaCluster(cluster) &&
      isClusterInPersonalTeam(cluster, teams) &&
      isClusterActive(cluster)
    );
  }

  isEditAccessListButtonDisabled(cluster: ClusterInfo): boolean {
    return (
      getClusterStatus(cluster) === ClusterStatus.PROVISIONING ||
      cluster.nebula?.status === NebulaClusterStatus.UPDATING
    );
  }

  /* Rename Cluster */
  isRenameButtonVisible(cluster: ClusterInfo): boolean {
    if (!cluster.features.includes(ClusterFeatures.CHANGE_TAG)) return false;
    return isNebulaCluster(cluster) ? isClusterActive(cluster) : true;
  }

  isRenameButtonDisabled(cluster: ClusterInfo): boolean {
    const status = getClusterStatus(cluster);
    return status === ClusterStatus.PROVISIONING || status !== ClusterStatus.ACTIVE;
  }

  /* Download Logs */
  isDownloadLogsButtonVisible(cluster: ClusterInfo): boolean {
    if (!cluster.features.includes(ClusterFeatures.LOGS_COLLECTION_SUPPORTED)) return false;
    return isNebulaCluster(cluster) ? isClusterActive(cluster) : true;
  }

  isDownloadLogsDisabled(cluster: ClusterInfo): boolean {
    const status = getClusterStatus(cluster);
    return status === ClusterStatus.PROVISIONING || status == ClusterStatus.DISCONNECTED;
  }

  // Update license
  public isUpdateLicenseButtonVisible(cluster: ClusterInfo): boolean {
    return cluster.features.includes(ClusterFeatures.LICENSE_UPLOAD) && !isNebulaCluster(cluster);
  }

  public isUpdateLicenseButtonDisabled(cluster: ClusterInfo): boolean {
    return getClusterStatus(cluster) !== ClusterStatus.ACTIVE;
  }

  /* Nebula Cluster Info */
  isNebulaClusterInfoButtonVisible(cluster: ClusterInfo): boolean {
    return isNebulaCluster(cluster);
  }

  /* Activate  cluster */
  isActivateClusterButtonVisible(cluster: ClusterInfo, teams: Team[]): boolean {
    return isCluster2x(cluster) && !cluster.info.active
      ? isNebulaCluster(cluster)
        ? isMeInNebulaTeam(teams) && isClusterActive(cluster)
        : true
      : false;
  }

  isActivateClusterButtonDisabled(cluster: ClusterInfo): boolean {
    const status = getClusterStatus(cluster);
    return status === ClusterStatus.DISCONNECTED || status === ClusterStatus.PROVISIONING;
  }

  /* Deactivate  cluster */
  isDeactivateClusterButtonVisible(cluster: ClusterInfo, teams: Team[]): boolean {
    return isCluster2x(cluster) && cluster.info.active
      ? isNebulaCluster(cluster)
        ? isMeInNebulaTeam(teams) && isClusterActive(cluster)
        : true
      : false;
  }

  isDeactivateClusterButtonDisabled(cluster: ClusterInfo): boolean {
    const status = getClusterStatus(cluster);
    return status === ClusterStatus.DISCONNECTED || status === ClusterStatus.PROVISIONING;
  }

  /* Destroy Nebula Cluster */
  isDestroyClusterButtonVisible(cluster: ClusterInfo, teams: Team[]): boolean {
    if (isMeInNebulaTeam(teams) && isNebulaCluster(cluster)) return true;
    return isClusterPersonal(teams, cluster) && isNebulaCluster(cluster) && isClusterActive(cluster);
  }

  isDestroyClusterButtonDisabled(cluster: ClusterInfo, teams: Team[]): boolean {
    if (isMeInNebulaTeam(teams)) return false;
    return getClusterStatus(cluster) === ClusterStatus.PROVISIONING;
  }

  /* Detach of Nebula Cluster attached via token */
  detachCluster(cluster: ClusterInfo) {
    this.store.dispatch(detachClusterIntent({ cluster }));
  }

  isDetachClusterButtonVisible = (cluster: ClusterInfo, teams: Team[]): boolean => {
    return !isNebulaCluster(cluster) && isClusterPersonal(teams, cluster);
  };

  // Suspend

  isSuspendClusterButtonVisible = (cluster: ClusterInfo, teams: Team[]): boolean => {
    const status = getClusterStatus(cluster);
    return (
      isNebulaCluster(cluster) &&
      !!status &&
      [
        ClusterStatus.DISCONNECTED,
        ClusterStatus.INACTIVE,
        ClusterStatus.ACTIVE,
        ClusterStatus.PROVISIONING,
        ClusterStatus.LIMITED,
      ].includes(status) &&
      (isClusterPersonal(teams, cluster) || isMeInNebulaTeam(teams))
    );
  };

  isSuspendClusterButtonDisabled = (cluster: ClusterInfo, teams: Team[]): boolean => {
    return (
      this.isSuspendClusterButtonVisible(cluster, teams) &&
      (getClusterStatus(cluster) === ClusterStatus.PROVISIONING ||
        cluster.nebula?.status === NebulaClusterStatus.UPDATING ||
        isPrivateLinkNebulaCluster(cluster) ||
        isAWSDataEncryptedCluster(cluster))
    );
  };

  suspendCluster(clusterId: ClusterId) {
    this.store.dispatch(suspendNebulaClusterIntent({ clusterId }));
  }

  // Resume

  public isResumeClusterButtonVisible(cluster: ClusterInfo, teams: Team[]): boolean {
    const status = getClusterStatus(cluster);
    return (
      isNebulaCluster(cluster) &&
      !!status &&
      [ClusterStatus.SUSPENDED, ClusterStatus.SUSPENDING].includes(status) &&
      (isClusterPersonal(teams, cluster) || isMeInNebulaTeam(teams))
    );
  }

  isResumeClusterButtonDisabled = (cluster: ClusterInfo, teams: Team[]): boolean => {
    return this.isResumeClusterButtonVisible(cluster, teams) && getClusterStatus(cluster) === ClusterStatus.SUSPENDING;
  };

  resumeCluster(clusterId: ClusterId) {
    this.store.dispatch(resumeSuspendedNebulaClusterIntent({ clusterId }));
  }

  // Initialize AI3 cluster

  isInitializeButtonVisible(cluster: ClusterInfo): boolean {
    if (isCluster2x(cluster)) return false;
    return cluster.status === ClusterStatus.UNINITIALIZED;
  }

  // 3x cluster connection settings

  areCcConnectionSettingsVisible = (clusterId: ClusterId): Signal<boolean> =>
    computed(() => {
      const cluster = this.store.selectSignal(clusterById(clusterId))();
      const teams = this.store.selectSignal(teamsSelector)();
      return (
        !!cluster && !!teams && isCluster3x(cluster) && teams.some(isClusterTeamOwnedByMe) && !isNebulaCluster(cluster)
      );
    });

  // Feature toggles, for Nebula team only

  areFeatureTogglesVisible = (cluster: ClusterInfo, teams: Team[]): boolean =>
    isCluster2x(cluster) ? isClusterInNebulaTeam(cluster) && isMeInNebulaTeam(teams) : false;

  onTracingEnableSlideToggleChange(clusterId: ClusterId, enabled: boolean) {
    this.store.dispatch(
      adminToggleClusterFeatures({
        clusterId,
        feature: 'tracing',
        enabled,
      }),
    );
  }
  onMetricsEnableSlideToggleChange(clusterId: ClusterId, enabled: boolean) {
    this.store.dispatch(
      adminToggleClusterFeatures({
        clusterId,
        feature: 'metrics',
        enabled,
      }),
    );
  }
  onComputeEnableSlideToggleChange(clusterId: ClusterId, enabled: boolean) {
    this.store.dispatch(
      adminToggleClusterFeatures({
        clusterId,
        feature: 'compute',
        enabled,
      }),
    );
  }
  onRunningQueriesEnableSlideToggleChange(clusterId: ClusterId, enabled: boolean) {
    this.store.dispatch(
      adminToggleClusterFeatures({
        clusterId,
        feature: 'runningQueries',
        enabled,
      }),
    );
  }

  // 3x configuration

  public readonly is3xConfigurationVisible = (cluster: ClusterInfo): boolean => {
    return isCluster3x(cluster);
  };

  public readonly is3xConfigurationDisabled = (cluster: ClusterInfo): boolean => {
    return !canClientsConnectToCluster(cluster);
  };
}
